import { DotV2 } from 'common/pill-v2/dot-v2.js';
import { PillDynamicWrapperV2 } from 'common/pill-v2/pill-dynamic-wrapper-v2.js';
import { PillPointer } from 'common/pill-v2/pill-pointer.js';
import { PillV2Label } from 'common/pill-v2/pill-v2-label.js';
import { MarkerV2SecondaryData } from 'common/pill-v2/pill-v2-secondary-data.js';
import { PillV2 } from 'common/pill-v2/pill-v2.js';
import { onClickStaticMapType } from 'components/map/map-controller/main/leaflet-click/click-updateSlot.js';
import { getMarkerV2ThemedPill, MarkerOptionsMap } from 'components/map/themes/pill-theme.js';
import { MarkerDisplaySize } from 'types/location.js';
import { MarkerPillPoint } from 'types/markerTypesV2/m-v2-core.js';
import { IMarkerV2 } from 'types/markerTypesV2/m-v2-general.js';
import { mergeMarkerTags } from './marker-pill-filter-v2';
import { getMarkerV2TextPos } from './marker-v2';
import { normalisePill } from './normalise-pill.js';
import { resolveDotSlotIndex } from './resolve-dot-slot-index.js';

export const MarkerPillV2 = ({
  markerDisplaySize,
  marker,
  selected,
  mockHover,
  markerThemes,
  onClick,
  markerTextOverride,
  staticMarkerId,
}: {
  marker: IMarkerV2<MarkerPillPoint>;
  markerDisplaySize: MarkerDisplaySize;
  selected?: boolean;
  mockHover?: boolean;
  markerThemes?: MarkerOptionsMap;
  onClick?: onClickStaticMapType;
  markerTextOverride?: string;
  staticMarkerId?: string;
}): JSX.Element => {
  const normalised = normalisePill(marker);
  const { slots, position, label, hasPoint, pillClass, tags } = normalised;
  const textPos = getMarkerV2TextPos(marker);

  const slotThemes = slots.map((slot) =>
    getMarkerV2ThemedPill({
      tags: mergeMarkerTags({ theme: slot.theme, tags }),
      meta: slot.meta ?? slot.text,
      icon: slot.icon,
      markerThemes,
      defaultTheme: { border: '#ffffff', background: '#ffffff', text: '#0B1940' },
    }),
  );
  const slot0 = slots[0];
  const slot0Theme = slotThemes[0];
  const pointerTheme = position.labelPos === 'left' ? slotThemes[slotThemes.length - 1] : slot0Theme;
  const isDot = markerDisplaySize === MarkerDisplaySize.Small;
  const dotTheme = isDot ? (slotThemes[resolveDotSlotIndex(slots, tags)] ?? slot0Theme) : slot0Theme;

  const labelText = markerTextOverride || label?.text;
  const hasLabelSecondary = marker.generatedWarningsList?.length || label?.suffix?.length || 0 > 1;
  const hasLabel = !!labelText || hasLabelSecondary;

  return (
    <PillDynamicWrapperV2
      displaySize={markerDisplaySize}
      selected={selected}
      mockHover={mockHover}
      staticPosition={position.staticPoint}
      labelPos={position.labelPos}
      markerType={normalised.type}
      hasPoint={hasPoint}
      offset={position.offset}
      isDot={isDot}
      onClick={onClick}
      textPos={textPos}
      staticMarkerId={staticMarkerId}
    >
      {isDot ? (
        <DotV2 themeProps={dotTheme} />
      ) : (
        <>
          {slots.map((slot, i) => (
            <PillV2
              key={i}
              icons={[slot.icon ?? slotThemes[i]?.icon]}
              className={`pill--border u-posRel ${pillClass ?? ''}`}
              label={slot.text}
              themeProps={slotThemes[i]}
            />
          ))}
          <PillPointer border={pointerTheme?.border} labelPos={position.labelPos} hasPoint={hasPoint} id={slot0.text} />
          {hasLabel && (
            <PillV2Label
              selected={selected}
              label={labelText}
              labelTextHover={label?.textHover}
              displaySize={markerDisplaySize}
              mockHover={mockHover}
              textPos={textPos}
            >
              <MarkerV2SecondaryData secondaryData={label?.suffix} warningsList={marker.generatedWarningsList} />
            </PillV2Label>
          )}
        </>
      )}
    </PillDynamicWrapperV2>
  );
};
